import { Button, CircularProgress } from '@mui/material';
import { memo, useEffect, useState } from 'react';

import DoughnutChart from 'components/ui/charts/DoughnutChart';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSearch from 'hooks/useSearch';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import LeadInfoModal from './LeadInfoModal';
import ContractInfoModal from './ContractInfoModal';
import LeadsSourceWidgetChart from './LeadsSourceWidgetChart';

// Lidlar ro'yxati bilan keladigan turlar ({counts, events}) — segmentga bosilganda LeadInfoModal ochiladi
const LEAD_LIST_TYPES = [
  'COMETOOFFICE',
  'REJECTEDLEAD',
  'TRASHREASON',
  'CONTRACTCONNECT',
  'OFFICEUNWORKED',
  'NOCONTACTLEAD',
];
// Shartnomalar ro'yxati bilan keladigan turlar ({result, contracts})
const CONTRACT_LIST_TYPES = ['CONTRACTSTAFF', 'CONTRACTOPERATOR'];

function getRandomColor() {
  var letters = '0123456789ABCDEF';
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export default memo(function Widget({ info, refetch, dragHandleProps }) {
  const query = useSearch();
  const { search } = useLocation();
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [values, setValues] = useState([]);
  const [colors, setColors] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [dataInfoModal, setDataInfoModal] = useState(false);

  // LEADSSOURCE stacked bar — o'z komponenti o'zi fetch qiladi (kun/oy/yil)
  const isLeadsSource = info?.type == 'LEADSSOURCE';

  const { data: widget, isLoading } = useQuery({
    queryKey: ['/admin/widgets/chart2', search, info?.id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/widgets/chart2${search ? search + '&' : '?'}type=${
          info?.type
        }&id=${info?.id}`,
      );
      return response.data.data;
    },
    enabled: !hasError && !!query.get('dashboard') && !!info && !isLeadsSource,
    onSuccess: (data) => {},
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  useEffect(() => {
    if (!widget) return;
    if (isLeadsSource) return; // stacked bar — o'z komponenti bilan render qilinadi

    if (
      !CONTRACT_LIST_TYPES.includes(info?.type) &&
      !LEAD_LIST_TYPES.includes(info?.type)
    ) {
      const labels = Object?.keys(widget);
      const values = Object?.values(widget);
      const colors = Object?.values(widget)?.map(() => getRandomColor());
      setData(
        Object?.entries(widget)?.map(([name, count], i) => ({
          name,
          value: count,
          color: colors?.[i],
        })),
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    } else if (LEAD_LIST_TYPES.includes(info?.type)) {
      const labels = Object.keys(widget?.counts || {});
      const values = Object?.values(widget?.counts);
      const events = labels?.map(
        (label) => widget?.events?.[label] || widget?.leads?.[label] || [],
      );

      const colors = labels?.map(() => getRandomColor());
      setData(
        labels?.map((item, i) => ({
          name: item,
          value: values?.[i],
          events: events?.[i],
          color: colors?.[i],
        })),
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    } else if (CONTRACT_LIST_TYPES.includes(info?.type)) {
      const labels = Object.keys(widget?.result);
      const values = Object?.values(widget?.result);
      const contracts = Object?.values(widget?.contracts)?.reduce(
        (acc, cur) => {
          return { ...acc, [cur.name]: cur?.contracts };
        },
        {},
      );

      const events = labels?.map((label) => contracts?.[label] || []);

      const colors = labels?.map(() => getRandomColor());
      setData(
        labels?.map((item, i) => ({
          name: item,
          value: values?.[i],
          contracts: events?.[i],
          color: colors?.[i],
        })),
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    }
  }, [widget]);

  const hasListModal =
    LEAD_LIST_TYPES.includes(info?.type) ||
    CONTRACT_LIST_TYPES.includes(info?.type);

  const onClick = (item) => {
    if (hasListModal) {
      setOpenInfoModal(true);
      setDataInfoModal(item);
    }
  };

  const onClickChart = (props) => {
    if (hasListModal) {
      const index = props?.[0]?.index;
      const info = data?.[index];
      setOpenInfoModal(true);
      setDataInfoModal(info);
    }
  };

  // Dizayn: 'classic' (hozirgidek) yoki 'compact' (ixcham, yon-legenda). Bazaga saqlanadi.
  const isCompact = info?.design === 'compact';

  const changeDesign = async (value) => {
    if (value === (info?.design || 'classic')) return;
    try {
      await axiosPrivate.post(
        `/admin/widgets/widgetupdate/${info?.id}`,
        JSON.stringify({ design: value }),
        { headers: { 'Content-Type': 'application/json' } },
      );
      refetch?.();
    } catch (e) {}
  };

  const designSwitch = (
    <select
      value={isCompact ? 'compact' : 'classic'}
      onChange={(e) => changeDesign(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className='text-[11px] border border-gray-200 rounded-md px-1.5 py-1 bg-white/90 text-gray-500 cursor-pointer outline-none'
    >
      <option value='classic'>Klassik</option>
      <option value='compact'>Ixcham</option>
    </select>
  );

  return (
    <div
      className={`relative ${
        isCompact
          ? 'bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden'
          : 'bg-white mb-4 rounded-lg'
      }`}
    >
      {/* Ko'chirish tutqichi (drag handle) */}
      {dragHandleProps && (
        <div
          {...dragHandleProps}
          className='absolute top-2 left-2 z-10 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing'
          title='Ko&#39;chirish'
        >
          <i className='bi bi-grip-vertical text-lg' />
        </div>
      )}

      {/* Dizayn tanlagich — bazaga saqlanadi */}
      <div className='absolute top-2 right-2 z-10'>{designSwitch}</div>

      {/* Sarlavha */}
      {isCompact ? (
        <div className='pt-4 pb-1 pl-8 pr-24'>
          <h2 className='text-[15px] font-semibold text-gray-700 truncate'>
            {info?.name}
          </h2>
        </div>
      ) : (
        <div className='flex items-center justify-center rounded-t-lg py-4 bg-gray-50 mb-2'>
          <h2>{info?.name}</h2>
        </div>
      )}

      {isLeadsSource ? (
        <LeadsSourceWidgetChart info={info} />
      ) : isLoading ? (
        <div className='h-[190px] flex items-center justify-center'>
          <CircularProgress size={28} color='inherit' />
        </div>
      ) : isCompact ? (
        /* IXCHAM — diagramma + yon legenda */
        <div className='flex items-center gap-3 px-4 pb-4 pt-1'>
          <div className='relative w-[130px] h-[130px] min-w-[130px]'>
            <DoughnutChart
              onClick={onClickChart}
              chartLabels={labels}
              chartDatasets={[
                {
                  data: values,
                  backgroundColor: colors,
                  borderWidth: 2,
                  hoverOffset: 10,
                  hoverBorderWidth: 0,
                  borderRadius: 4,
                  cutout: '70%',
                },
              ]}
              suffix=''
              optionPlugins={{ legend: { display: false } }}
              options={{ layout: { padding: { top: 6, bottom: 6 } } }}
            />
            <div className='text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
              <div className='text-[20px] font-bold text-gray-800 leading-none'>
                {total.toLocaleString()}
              </div>
              <div className='text-[10px] text-gray-400 mt-0.5'>
                {t('common.table.total')}
              </div>
            </div>
          </div>
          <div className='flex-1 min-w-0 text-sm overflow-y-auto my-scroll max-h-[150px]'>
            {data?.map((item, i) => (
              <div
                key={`${i}legend`}
                onClick={() => onClick(item)}
                className='flex items-center justify-between gap-2 py-1.5 px-1 rounded-md cursor-pointer hover:bg-gray-50'
              >
                <div className='flex items-center gap-2 min-w-0'>
                  <span
                    className='w-2.5 h-2.5 rounded-[3px] shrink-0'
                    style={{ backgroundColor: item?.color }}
                  />
                  <span className='truncate text-gray-600'>{item?.name}</span>
                </div>
                <span className='shrink-0 font-medium text-[#0057FF]'>
                  {item?.value?.toLocaleString?.() ?? item?.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* KLASSIK — diagramma + pastda ro'yxat (hozirgidek) */
        <div className={'flex gap-4 flex-col items-center py-4 px-4'}>
          <div className='relative w-full aspect-square max-w-[300px] mx-auto'>
            <DoughnutChart
              onClick={onClickChart}
              chartLabels={labels}
              chartDatasets={[
                {
                  data: values,
                  backgroundColor: colors,
                  borderWidth: 2,
                  hoverOffset: 15,
                  hoverBorderWidth: 0,
                  borderRadius: 4,
                },
              ]}
              suffix=''
              optionPlugins={{ legend: { display: false } }}
              options={{ layout: { padding: { top: 10, bottom: 10 } } }}
            />
            <div className='text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
              <h1 className='text-[35px]'>{total.toLocaleString()}</h1>
              <p className='text-sm text-gray-500'>{t('common.table.total')}</p>
            </div>
          </div>
          <div className='text-sm overflow-x-auto my-scroll w-full max-h-[500px]'>
            <Button
              fullWidth
              className='!justify-between !text-gray-700 !bg-gray-100'
            ></Button>
            {data?.map((item, i) => (
              <div
                key={`${i}hodimlar`}
                className='flex justify-between text-[16px] font-medium text-[#464255] w-full py-3 px-2 my-1.5 cursor-pointer hover:bg-gray-50 rounded-lg'
                onClick={() => {
                  onClick(item);
                }}
              >
                <div className='flex items-center whitespace-nowrap'>
                  <div
                    className={`min-w-[18px] mr-1 h-[18px] border-4 rounded-full`}
                    style={{ borderColor: item?.color }}
                  />
                  {item?.name}
                </div>
                <div className='flex items-center whitespace-nowrap'>
                  {item?.value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {LEAD_LIST_TYPES.includes(info?.type) && (
        <LeadInfoModal
          open={openInfoModal}
          setOpen={setOpenInfoModal}
          data={dataInfoModal}
          setData={setDataInfoModal}
        />
      )}
      {CONTRACT_LIST_TYPES.includes(info?.type) && (
        <ContractInfoModal
          open={openInfoModal}
          setOpen={setOpenInfoModal}
          data={dataInfoModal}
          setData={setDataInfoModal}
        />
      )}
    </div>
  );
});
